import React, { useEffect, useState, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Pause, Play, ChevronLeft, ChevronRight, Maximize, Minimize, X, Camera, Images } from 'lucide-react';
import { Photo } from '../../types';
import styles from './LiveSlideshowModal.module.css';

type SlideshowMode = 'slideshow' | 'booth';

interface LiveSlideshowModalProps {
  isOpen: boolean;
  onClose: () => void;
  photos: Photo[];
  albumName: string;
}

const SPEED_OPTIONS = [3, 5, 8, 10] as const;
type Speed = (typeof SPEED_OPTIONS)[number];

const MAX_SLIDES = 50;
const CROSSFADE_MS = 600;
const IDLE_TIMEOUT_MS = 3000;

export function LiveSlideshowModal({ isOpen, onClose, photos, albumName }: LiveSlideshowModalProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [speed, setSpeed] = useState<Speed>(5);
  const [paused, setPaused] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const [direction, setDirection] = useState<1 | -1>(1);
  const [mode, setMode] = useState<SlideshowMode>('slideshow');
  const [showFlash, setShowFlash] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);
  const idleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const autoAdvanceRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const prevPhotoCountRef = useRef<number>(0);

  // Take newest photos first, capped at MAX_SLIDES
  const slidePhotos = photos.slice(0, MAX_SLIDES);
  const totalSlides = slidePhotos.length;

  // ── Reset on open ─────────────────────────────────────────────────────────
  useEffect(() => {
    if (isOpen) {
      setCurrentIndex(0);
      setPaused(false);
      setShowControls(true);
      setDirection(1);
    }
  }, [isOpen]);

  // ── Fullscreen API ────────────────────────────────────────────────────────
  const requestFullscreen = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;
    if (el.requestFullscreen) {
      el.requestFullscreen().catch(() => {/* silent — user gesture may be needed */});
    } else if ((el as any).webkitRequestFullscreen) {
      (el as any).webkitRequestFullscreen();
    }
  }, []);

  const exitFullscreen = useCallback(() => {
    if (document.fullscreenElement) {
      document.exitFullscreen().catch(() => {});
    } else if ((document as any).webkitFullscreenElement) {
      (document as any).webkitExitFullscreen();
    }
  }, []);

  // Request fullscreen on open
  useEffect(() => {
    if (isOpen) {
      // Small delay to ensure the DOM element is rendered
      const timer = setTimeout(() => requestFullscreen(), 100);
      return () => clearTimeout(timer);
    }
  }, [isOpen, requestFullscreen]);

  // Listen for fullscreen changes
  useEffect(() => {
    const handleChange = () => {
      setIsFullscreen(!!document.fullscreenElement || !!(document as any).webkitFullscreenElement);
    };
    document.addEventListener('fullscreenchange', handleChange);
    document.addEventListener('webkitfullscreenchange', handleChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleChange);
      document.removeEventListener('webkitfullscreenchange', handleChange);
    };
  }, []);

  // ── Navigation ────────────────────────────────────────────────────────────
  const goNext = useCallback(() => {
    if (totalSlides <= 1) return;
    setDirection(1);
    setCurrentIndex((i) => (i + 1) % totalSlides);
  }, [totalSlides]);

  const goPrev = useCallback(() => {
    if (totalSlides <= 1) return;
    setDirection(-1);
    setCurrentIndex((i) => (i - 1 + totalSlides) % totalSlides);
  }, [totalSlides]);

  // ── Auto-advance ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (autoAdvanceRef.current) {
      clearInterval(autoAdvanceRef.current);
      autoAdvanceRef.current = null;
    }
    if (!isOpen || paused || totalSlides <= 1) return;

    autoAdvanceRef.current = setInterval(() => {
      setDirection(1);
      setCurrentIndex((i) => (i + 1) % totalSlides);
    }, speed * 1000);

    return () => {
      if (autoAdvanceRef.current) {
        clearInterval(autoAdvanceRef.current);
        autoAdvanceRef.current = null;
      }
    };
  }, [isOpen, paused, speed, totalSlides]);

  // ── Idle detection (auto-hide controls + cursor) ──────────────────────────
  const resetIdleTimer = useCallback(() => {
    setShowControls(true);
    if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    idleTimerRef.current = setTimeout(() => {
      setShowControls(false);
    }, IDLE_TIMEOUT_MS);
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    resetIdleTimer();
    return () => {
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    };
  }, [isOpen, resetIdleTimer]);

  const handleMouseMove = useCallback(() => {
    resetIdleTimer();
  }, [resetIdleTimer]);

  const handleClick = useCallback(() => {
    // Tap to show controls if hidden, or close if visible for long
    if (!showControls) {
      resetIdleTimer();
    }
  }, [showControls, resetIdleTimer]);

  // ── Keyboard ──────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      switch (e.key) {
        case 'Escape':
          exitFullscreen();
          onClose();
          break;
        case 'ArrowRight':
          goNext();
          resetIdleTimer();
          break;
        case 'ArrowLeft':
          goPrev();
          resetIdleTimer();
          break;
        case ' ':
          e.preventDefault();
          setPaused((p) => !p);
          resetIdleTimer();
          break;
        case 'f':
        case 'F':
          if (isFullscreen) {
            exitFullscreen();
          } else {
            requestFullscreen();
          }
          resetIdleTimer();
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, goNext, goPrev, onClose, isFullscreen, requestFullscreen, exitFullscreen, resetIdleTimer]);

  // ── Cleanup fullscreen on close ───────────────────────────────────────────
  useEffect(() => {
    if (!isOpen && isFullscreen) {
      exitFullscreen();
    }
  }, [isOpen, isFullscreen, exitFullscreen]);

  // ── Image preloading ──────────────────────────────────────────────────────
  useEffect(() => {
    if (!isOpen || totalSlides === 0) return;
    // Preload the next photo
    const nextIndex = (currentIndex + 1) % totalSlides;
    const nextPhoto = slidePhotos[nextIndex];
    const url = nextPhoto?.processedUrl ?? nextPhoto?.originalUrl ?? nextPhoto?.thumbnailUrl;
    if (url) {
      const img = new Image();
      img.src = url;
    }
  }, [isOpen, currentIndex, totalSlides, slidePhotos]);

  // ── Booth flash helper ─────────────────────────────────────────────────
  const triggerFlash = useCallback(() => {
    setShowFlash(true);
    const timer = setTimeout(() => setShowFlash(false), 100);
    return () => clearTimeout(timer);
  }, []);

  // ── New photo arrival detection (booth mode) ─────────────────────────────
  useEffect(() => {
    if (!isOpen) {
      prevPhotoCountRef.current = 0;
      return;
    }
    const prevCount = prevPhotoCountRef.current;
    prevPhotoCountRef.current = totalSlides;

    // If photos were added (not initial load) and we're in booth mode,
    // jump to the newest photo (index 0 since slidePhotos is newest-first)
    if (mode === 'booth' && prevCount > 0 && totalSlides > prevCount) {
      setDirection(1);
      setCurrentIndex(0);
      triggerFlash();
    }
  }, [isOpen, totalSlides, mode, triggerFlash]);

  if (!isOpen || totalSlides === 0) return null;

  const currentPhoto = slidePhotos[currentIndex];
  const photoUrl = currentPhoto?.processedUrl ?? currentPhoto?.originalUrl ?? currentPhoto?.thumbnailUrl ?? '';

  return (
    <div
      ref={containerRef}
      className={`${styles['container']} ${showControls ? styles['cursor-default'] : styles['cursor-none']}`}
      onMouseMove={handleMouseMove}
      onClick={handleClick}
    >
      {/* Photo display — crossfade (slideshow) or dramatic entrance (booth) */}
      <AnimatePresence mode="popLayout" initial={false}>
        {mode === 'slideshow' ? (
          <motion.img
            key={`slide-${currentPhoto?._id}-${currentIndex}`}
            src={photoUrl}
            alt={currentPhoto?.originalFilename ?? 'Slideshow photo'}
            className={styles['photo']}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: CROSSFADE_MS / 1000, ease: 'easeInOut' }}
            draggable={false}
          />
        ) : (
          <motion.img
            key={`booth-${currentPhoto?._id}-${currentIndex}`}
            src={photoUrl}
            alt={currentPhoto?.originalFilename ?? 'Slideshow photo'}
            className={`${styles['photo']} ${styles['booth-photo-initial']}`}
            initial={{ opacity: 0, scale: 0.3, rotate: -3 }}
            animate={{
              opacity: 1,
              scale: 1,
              rotate: 0,
              filter: 'drop-shadow(0 25px 60px rgba(0,0,0,0.6))',
              transition: {
                default: {
                  type: 'spring',
                  stiffness: 200,
                  damping: 15,
                },
                opacity: { duration: 0.2, ease: 'easeOut' },
                filter: { duration: 0.5, ease: 'easeOut' },
              },
            }}
            exit={{
              opacity: 0,
              scale: 0.8,
              transition: { duration: 0.25, ease: 'easeIn' },
            }}
            onAnimationStart={() => {
              if (mode === 'booth') triggerFlash();
            }}
            draggable={false}
          />
        )}
      </AnimatePresence>

      {/* Booth mode: camera flash overlay */}
      <AnimatePresence>
        {showFlash && (
          <motion.div
            className={styles['flash-overlay']}
            initial={{ opacity: 0.85 }}
            animate={{ opacity: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.1, ease: 'easeOut' }}
          />
        )}
      </AnimatePresence>

      {/* Close button (top-right) */}
      <AnimatePresence>
        {showControls && (
          <motion.button
            className={styles['close-button']}
            onClick={(e) => {
              e.stopPropagation();
              exitFullscreen();
              onClose();
            }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            aria-label="Close slideshow"
          >
            <X className={styles['icon-xl']} />
          </motion.button>
        )}
      </AnimatePresence>

      {/* Left/Right navigation arrows (sides) */}
      <AnimatePresence>
        {showControls && totalSlides > 1 && (
          <>
            <motion.button
              className={styles['nav-prev']}
              onClick={(e) => {
                e.stopPropagation();
                goPrev();
                resetIdleTimer();
              }}
              initial={{ opacity: 0, x: -8 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -8 }}
              transition={{ duration: 0.2 }}
              aria-label="Previous photo"
            >
              <ChevronLeft className={styles['icon-xl']} />
            </motion.button>

            <motion.button
              className={styles['nav-next']}
              onClick={(e) => {
                e.stopPropagation();
                goNext();
                resetIdleTimer();
              }}
              initial={{ opacity: 0, x: 8 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 8 }}
              transition={{ duration: 0.2 }}
              aria-label="Next photo"
            >
              <ChevronRight className={styles['icon-xl']} />
            </motion.button>
          </>
        )}
      </AnimatePresence>

      {/* Bottom control bar */}
      <AnimatePresence>
        {showControls && (
          <motion.div
            className={styles['bottom-bar']}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 12 }}
            transition={{ duration: 0.25, ease: 'easeOut' }}
          >
            <div className={styles['bottom-bar-inner']}>
              <div className={styles['controls-row']}>
                {/* Left: Album name + counter */}
                <div className={styles['album-info']}>
                  <span className={styles['album-name']}>
                    {albumName}
                  </span>
                  <span className={styles['counter']}>
                    {currentIndex + 1} / {totalSlides}
                  </span>
                </div>

                {/* Center: Play/pause + speed pills */}
                <div className={styles['center-controls']}>
                  {/* Play/Pause */}
                  <button
                    className={styles['play-pause-button']}
                    onClick={(e) => {
                      e.stopPropagation();
                      setPaused((p) => !p);
                      resetIdleTimer();
                    }}
                    aria-label={paused ? 'Resume slideshow' : 'Pause slideshow'}
                  >
                    {paused ? (
                      <Play className={`${styles['play-icon-offset']} ${styles['icon-lg']}`} />
                    ) : (
                      <Pause className={styles['icon-lg']} />
                    )}
                  </button>

                  {/* Speed pills */}
                  <div className={styles['speed-pills']}>
                    {SPEED_OPTIONS.map((s) => (
                      <button
                        key={s}
                        className={[
                          styles['speed-pill'],
                          s === speed ? styles['speed-pill-active'] : '',
                        ].filter(Boolean).join(' ')}
                        onClick={(e) => {
                          e.stopPropagation();
                          setSpeed(s);
                          resetIdleTimer();
                        }}
                        aria-label={`${s} second interval`}
                      >
                        {s}s
                      </button>
                    ))}
                  </div>
                </div>

                {/* Mode toggle: Slideshow | Photo Booth */}
                <div className={styles['mode-toggle']}>
                  <button
                    className={[
                      styles['mode-button'],
                      mode === 'slideshow' ? styles['mode-button-active'] : '',
                    ].filter(Boolean).join(' ')}
                    onClick={(e) => {
                      e.stopPropagation();
                      setMode('slideshow');
                      resetIdleTimer();
                    }}
                    aria-label="Slideshow mode"
                  >
                    <Images className={styles['icon-sm']} />
                    <span className={styles['mode-label']}>Slideshow</span>
                  </button>
                  <button
                    className={[
                      styles['mode-button'],
                      mode === 'booth' ? styles['mode-button-active'] : '',
                    ].filter(Boolean).join(' ')}
                    onClick={(e) => {
                      e.stopPropagation();
                      setMode('booth');
                      resetIdleTimer();
                    }}
                    aria-label="Photo Booth mode"
                  >
                    <Camera className={styles['icon-sm']} />
                    <span className={styles['mode-label']}>Photo Booth</span>
                  </button>
                </div>

                {/* Right: Fullscreen toggle */}
                <button
                  className={styles['fullscreen-button']}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (isFullscreen) {
                      exitFullscreen();
                    } else {
                      requestFullscreen();
                    }
                    resetIdleTimer();
                  }}
                  aria-label={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
                >
                  {isFullscreen ? (
                    <Minimize className={styles['icon-lg']} />
                  ) : (
                    <Maximize className={styles['icon-lg']} />
                  )}
                </button>
              </div>

              {/* Progress bar — shows auto-advance progress */}
              {!paused && totalSlides > 1 && (
                <div className={styles['progress-wrapper']}>
                  <div className={styles['progress-track']}>
                    <motion.div
                      className={styles['progress-fill']}
                      key={`progress-${currentIndex}-${speed}-${paused}`}
                      initial={{ width: '0%' }}
                      animate={{ width: '100%' }}
                      transition={{ duration: speed, ease: 'linear' }}
                    />
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Photo Booth badge (top-left, always visible in booth mode) */}
      <AnimatePresence>
        {mode === 'booth' && (
          <motion.div
            className={styles['booth-badge']}
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            transition={{ type: 'spring', stiffness: 300, damping: 20 }}
          >
            <Camera className={`${styles['booth-badge-icon']} ${styles['icon-md']}`} />
            <span className={styles['booth-badge-text']}>
              Photo Booth
            </span>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Keyboard hints — shown briefly on first open */}
      <AnimatePresence>
        {showControls && (
          <motion.div
            className={[
              styles['keyboard-hints'],
              mode === 'booth' ? styles['keyboard-hints-booth'] : '',
            ].filter(Boolean).join(' ')}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
          >
            <span className={styles['keyboard-hints-text']}>
              Space pause &middot; Arrows nav &middot; F fullscreen &middot; Esc close
            </span>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
